import { useEffect, useState } from "react";
import api from "@/api";
import { useAuth, ROLE_LABELS } from "@/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function UsersPage() {
  const { subaccount } = useAuth();
  const [users, setUsers] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "coord_municipal",
    region_type: "", region_name: "", municipio_clave: "",
  });

  const load = async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  };

  useEffect(() => {
    load();
    if (subaccount?.entidad_clave) {
      api.get("/catalog/municipios", { params: { entidad_clave: subaccount.entidad_clave } })
        .then((r) => setMunicipios(r.data));
    }
  }, [subaccount?.entidad_clave]);

  const enabled = (subaccount?.enabled_roles || []).filter((r) => r !== "admin_candidato");
  const needsRegion = ["coord_regional", "coord_colonia"].includes(form.role);
  const needsMunicipio =
    form.role === "coord_municipal" ||
    ((form.role === "coord_colonia" || form.role === "coord_regional") && subaccount?.scope === "estatal");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", {
        ...form,
        municipio_clave: form.municipio_clave ? parseInt(form.municipio_clave, 10) : null,
        region_type: form.region_type || null,
        region_name: form.region_name || null,
      });
      toast.success("Usuario creado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "coord_municipal", region_type: "", region_name: "", municipio_clave: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error creando usuario");
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este usuario?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="tracking-label text-blue-700">Estructura</span>
          <h1 className="mt-2 font-display font-black text-3xl text-slate-900">Usuarios y roles</h1>
          <p className="text-sm text-slate-600 mt-1">{users.length} usuarios activos en la subcuenta</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="users-add-button">
              <Plus className="w-4 h-4 mr-1.5" /> Agregar usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Crear usuario</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="users-form">
              <div>
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="users-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {enabled.map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nombre</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="users-input-name" /></div>
              <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="users-input-email" /></div>
              <div><Label>Contraseña</Label><Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="users-input-password" /></div>
              {needsMunicipio && (
                <div>
                  <Label>Municipio</Label>
                  <Select value={String(form.municipio_clave || "")} onValueChange={(v) => setForm({ ...form, municipio_clave: v })}>
                    <SelectTrigger data-testid="users-muni-select"><SelectValue placeholder="Selecciona municipio" /></SelectTrigger>
                    <SelectContent>
                      {municipios.map((m) => (
                        <SelectItem key={m.municipio_clave} value={String(m.municipio_clave)}>{m.municipio_nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {needsRegion && (
                <>
                  <div>
                    <Label>Tipo de región</Label>
                    <Select value={form.region_type} onValueChange={(v) => setForm({ ...form, region_type: v })}>
                      <SelectTrigger data-testid="users-region-type"><SelectValue placeholder="zona o colonia" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="zona">Zona / Región</SelectItem>
                        <SelectItem value="colonia">Colonia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Nombre de la región / colonia</Label><Input required value={form.region_name} onChange={(e) => setForm({ ...form, region_name: e.target.value })} data-testid="users-region-name" /></div>
                </>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" className="btn-primary" data-testid="users-submit">Crear</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-card overflow-hidden">
        {users.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Sin usuarios.</div>
        ) : (
          <div className="divide-y divide-slate-200">
            {users.map((u) => (
              <div key={u.id} className="px-6 py-4 flex items-center justify-between gap-3" data-testid={`users-row-${u.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{ROLE_LABELS[u.role]}</Badge>
                    {u.region_name && (
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">{u.region_type}: {u.region_name}</Badge>
                    )}
                    {u.municipio_nombre && <Badge variant="outline">{u.municipio_nombre}</Badge>}
                  </div>
                </div>
                {u.role !== "admin_candidato" && (
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => remove(u.id)} data-testid={`users-delete-${u.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
